import { useEffect, useState } from "react";
import axios from "axios";
import { USE_MOCK, API_BASE } from '@/utils/constants';
import useSnackbar from '@/hooks/useSnackbar'
import Captcha from "../components/common/Captcha";
import { httpErrorHandler } from "../utils/httpErrorHandler";
import { useNavigate } from "react-router-dom";

import "../assets/styles/LoginPage.css";

import heroImg from "@/assets/images/lamp_icon.png";
import { ReactComponent as ChatBotLogo } from '@/assets/images/chat-bot-logo.svg'
import dotsBlock from "../assets/images/dot.png";
import openEye from "../assets/images/eyeopen.png";
import greyEye from "../assets/images/eyegrey.png";
import closedEye from "../assets/images/eyeclose.png";
import lockIcon from "../assets/images/lock.png";
import userIcon from "../assets/images/user.png";
import logo from "../assets/images/logo.png";


const normalizeBase64 = (raw) => {
    if (!raw || typeof raw !== "string") return null;
    
    // Remove any whitespace/newlines
    const clean = raw.trim().replace(/\s+/g, '');
    
    // Check if it already has data URL prefix
    if (clean.startsWith("data:image/")) {
        return clean;
    }
    
    // Some APIs might return base64 without proper padding
    // Add padding if needed for base64
    let padded = clean;
    while (padded.length % 4 !== 0) {
        padded += '=';
    }
    
    return `data:image/jpeg;base64,${padded}`;
};

export default function LoginPage({ fetchUser, onLogin }) {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [remember, setRemember] = useState(true);
	const [showPass, setShowPass] = useState(false);
	const { showSnackbar } = useSnackbar();

	const [captchaId, setCaptchaId] = useState(null);
	const [captchaValue, setCaptchaValue] = useState("");
	const [imgSrc, setImgSrc] = useState(null);
	const [loading, setLoading] = useState(false);

	const navigate = useNavigate();

	const fetchCaptcha = async () => {
		setLoading(true);
		axios.get(`${API_BASE}/auth/captcha`)
			.then(response => {
				const data = response.data
				setCaptchaId(data.id)
				const src = normalizeBase64(data.image)
				setImgSrc(src)

			}).catch((err) => {
				httpErrorHandler({
					err: err,
					setSnackbar: (s) => showSnackbar(s?.message || '', { type: s?.type || 'error' }),
					navigate: navigate
				});
			}).finally(setLoading(false))
	};

	useEffect(() => {
		const saved = localStorage.getItem("remembered_email");
		if (saved) setEmail(saved);
	}, []);

	const handleSubmit = async (e) => {
		e.preventDefault();

		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(email)) {
			showSnackbar('فرمت ایمیل نامعتبر است', { type: 'error' });
			return;
		}

		if (remember) localStorage.setItem("remembered_email", email);
		else localStorage.removeItem("remembered_email");

		if (USE_MOCK) {
			localStorage.setItem("token", "mock-token");
			onLogin?.({ email, accessLevel: "high" });
			return;
		}

		axios.post(
			`${API_BASE}/auth/login`,
			{
				email: email,
				password: password,
				captcha_value: captchaValue,
				captcha_id: captchaId
			}
		)
			.then(response => {
				const data = response.data;
				const token = data?.token;
				const userId = data?.user_id;

				if (userId) localStorage.setItem("user_id", userId);
				if (token) localStorage.setItem("token", token);

				onLogin?.({ email: email });
				
				fetchUser()

				navigate('/');
			})
			.catch(error => {
				httpErrorHandler({
					err: error,
					errorKeys: ['email', 'password'],
					setFieldErrors: {},
					setSnackbar: (s) => showSnackbar(s?.message || '', { type: s?.type || 'error' }),
					navigate: navigate
				});
				setCaptchaValue("")
				fetchCaptcha()
			});
	};

	const eyeSrc = password.length === 0 ? greyEye : showPass ? openEye : closedEye;
	const eyeAlt =
		password.length === 0 ? "چشم غیرفعال" : showPass ? "مخفی‌کردن رمز" : "نمایش رمز";

	return (
		<div className="app">
			<div className="shell">
				<main className="page">
					{/* چپ */}
					<section className="left">
						<header className="brand">
							<span className="brand-text">زیست‌یار</span>
							<ChatBotLogo />
							{/* <img src={logoShape} alt="" className="brand-mark" />                 */}
						</header>

						<img src={dotsBlock} alt="" className="dots-block dots-block--lower" />
						<img src={dotsBlock} alt="" className="dots-block dots-block--square" />
						<img src={heroImg} alt="دست ربات و نقشه" className="hero" draggable="false" />

						{/* <div className="welcome">
                <h1 className="hello">،سلام</h1>
                <p className="lead">به سامانه دستیار هوشمند محیط‌زیست خوش آمدید</p>
                {/* <p className="desc">
                  این سامانه با هدف ارائه مشاوره، آموزش و پایش شاخص‌های محیط‌زیستی طراحی شده است. با ورود به حساب کاربری،
                  می‌توانید از خدمات هوشمند ما در زمینه حفاظت از منابع طبیعی، مدیریت پسماند و کاهش اثرات زیست‌محیطی بهره‌مند شوید
                </p> 
              </div> */}
					</section>

					{/* راست */}
					<section className="right">
						<div className="auth-card">
							<div className="card-logo">
								<img src={logo} alt="logo" />

							</div>
							<form className="form" onSubmit={handleSubmit}>
								{/* ایمیل */}
								<div className="login-input-wrapper">
									<label className="field">
										<span className="field-label with-icon-end">
											<span className="label-text">نام کاربری (ایمیل یا شماره همراه)</span>
											<img src={userIcon} alt="" className="label-icon" />
										</span>

										<div className="input-wrap">
											<input
												type="email"
												placeholder="نام کاربری خود را وارد کنید"
												value={email}
												onChange={(e) => setEmail(e.target.value.trim())}
												required
												autoComplete="username"
											/>
										</div>
									</label>
									{/* <span className="error-line">ایمیل به درستی وارد نشده است</span> */}
								</div>

								{/* پسورد */}
								<div className="login-input-wrapper">
									<label className="field">
										<span className="field-label with-icon-end">
											<span className="label-text">رمز عبور</span>
											<img src={lockIcon} alt="" className="label-icon" />
										</span>

										<div className="input-wrap has-eye">
											<input
												type={showPass ? "text" : "password"}
												placeholder="رمز عبور خود را وارد کنید"
												value={password}
												onChange={(e) => setPassword(e.target.value)}
												required
												autoComplete="current-password"
											/>
											<button
												type="button"
												className="eye-btn"
												onClick={() => password && setShowPass(v => !v)}
												disabled={!password}
												aria-label={eyeAlt}
												title={eyeAlt}
											>
												<img src={eyeSrc} alt="" />
											</button>
										</div>
									</label>
									{/* <span className="error-line">ایمیل به درستی وارد نشده است</span> */}
								</div>

								<Captcha
									setId={setCaptchaId}
									onChange={setCaptchaValue}
									fetchCaptcha={fetchCaptcha}
									value={captchaValue}
									setValue={setCaptchaValue}
									imgSrc={imgSrc}
									loading={loading}
								/>

								<div className="field-row">
									<label className="remember-inline">
										<input
											type="checkbox"
											checked={remember}
											onChange={(e) => setRemember(e.target.checked)}
										/>
										<span>مرا به خاطر بسپار</span>
									</label>
									<a className="link forget" href="#forgot">فراموشی رمز عبور</a>
								</div>



								<div className="btn-group">
									<button type="submit" className="btn primary">ورود</button>
									<button type="button" className="btn outline">ورود از طریق دولت من</button>
								</div>

								{/* <p className="signup">
									اگر حساب کاربری ندارید؟{" "}
									<button
										type="button"
										className="link-btn"
										onClick={() => navigate("/signup")}
									>
										ثبت‌نام
									</button>
								</p> */}


							</form>
						</div>
						<div className="powered-by">
							Powered by: ICT-TMU
						</div>
					</section>
				</main>
			</div>
			{/* Global snackbar handled by SnackbarProvider */}
		</div>
	);
}